// src/data/claimSections.js
//
// BOB, 2026-10-25: "the claim page appearance for the membership needs something expressly for their
// ease of use, visually appealing certainly but not overly busy, simple so perhaps each section can be
// presented as popups instead of having to scroll like mad - personal information, employer
// information, union information, all as it is currently administered except now 3 separate pop ups."
//
// This is the model behind that: the three groups, which fields are in each, which of them the claim
// cannot be filed without, and which the platform fills in by itself. It moves no field and changes no
// rule - the same fields, the same validator, in three doors instead of one long scroll.
//
// THE THIRD SECTION IS DIFFERENT AND THE SCREEN MUST SAY SO. A member does not type his General
// Committee, his Local Committee or his agreement: the platform derives all three from his railroad,
// craft and work location (NewClaim.js, the effect at line 152). So the union section is a CONFIRMATION,
// not a form - a man who is asked to fill in what the platform already knows will either leave it or
// guess at it, and a guessed committee routes a claim to the wrong officer.
//
// Pure. Proved by scripts\test-claim-sections.mjs.

// The three required fields are the validator's own, read from NewClaim.js: claimantName, railroad,
// workLocation. Nothing else is marked required here, because nothing else is required there.
const REQUIRED = Object.freeze(['claimantName', 'railroad', 'workLocation', 'incidentDate', 'description']);

export const FIELD_LABELS = Object.freeze({
  claimantName: 'Full name',
  claimantStreet: 'Street address',
  claimantCity: 'City',
  claimantState: 'State',
  claimantZip: 'ZIP',
  claimantPhone: 'Cell phone',
  claimantEmail: 'E-mail address',
  railroad: 'Railroad',
  workLocation: 'Work location',
  craft: 'Craft',
  rosterNumber: 'Roster number',
  seniorityDate: 'Seniority date',
  employeeNumber: 'Employee number',
  claimantJobSymbol: 'Job symbol',
  claimantTourOfDuty: 'Tour of duty',
  generalCommittee: 'General Committee',
  localCommittee: 'Local Committee',
  applicableAgreement: 'Agreement',
});

export const SECTIONS = Object.freeze([
  Object.freeze({
    key: 'personal',
    title: 'Personal information',
    why: 'Who you are and where the Organization can reach you about this claim.',
    fields: Object.freeze(['claimantName', 'claimantStreet', 'claimantCity', 'claimantState', 'claimantZip', 'claimantPhone', 'claimantEmail']),
    derived: Object.freeze([]),
  }),
  Object.freeze({
    key: 'employer',
    title: 'Employer information',
    why: 'Where you work and how the carrier knows you - this is what ties the claim to your seniority.',
    fields: Object.freeze(['railroad', 'workLocation', 'craft', 'rosterNumber', 'seniorityDate', 'employeeNumber', 'claimantJobSymbol', 'claimantTourOfDuty']),
    derived: Object.freeze([]),
  }),
  Object.freeze({
    key: 'union',
    title: 'Union information',
    why: 'Which committees and which agreement your claim falls under. The platform works these out from '
      + 'your railroad, craft and work location - read them, and tell your Local Chairperson if any of '
      + 'them is wrong. Do not guess: a wrong committee sends your claim to the wrong officer.',
    fields: Object.freeze(['generalCommittee', 'localCommittee', 'applicableAgreement']),
    derived: Object.freeze(['generalCommittee', 'localCommittee', 'applicableAgreement']),
  }),
]);

// THE REST OF THE CLAIM FORM, GROUPED THE SAME WAY (Bob, 2026-10-25: "I want to treat the rest of the
// claim form to be handled in the same manner, grouping it intelligently and accordingly").
//
// The groups are not invented: they are the wizard's OWN section titles, read from NewClaim.js -
// "What Happened" (line 998), "Subject of Claim & Parties Involved" (1159), "Remedy Sought /
// Valuation" (1280), "Exhibits & Evidence" (1146) - and the basis-of-claim boxes. Each keeps the
// fields that screen already holds; nothing moves between groups and no field is added or dropped.
export const CLAIM_SECTIONS = Object.freeze([
  Object.freeze({
    key: 'happened',
    title: 'What happened',
    why: 'The day, the time, the place and what took place, in your own words. This is the part of the '
      + 'claim nobody else can write for you.',
    fields: Object.freeze(['incidentDate', 'incidentTime12', 'yardOfIncident', 'eventLocation', 'shiftLength', 'shiftEnd', 'description']),
    derived: Object.freeze([]),
  }),
  Object.freeze({
    key: 'basis',
    title: 'The rule you say was broken',
    why: 'Which part of your agreement this claim rests on. The platform offers the rules of your own '
      + 'agreement; no General Chairperson has confirmed that text yet, so read it rather than trust it.',
    fields: Object.freeze(['violationType', 'violationTypeOther', 'rulesViolated']),
    derived: Object.freeze([]),
  }),
  Object.freeze({
    key: 'parties',
    title: 'Who else was involved',
    why: 'Who performed the work, and anyone who saw it. A name here is a fact about the claim, not an '
      + 'accusation about a person.',
    fields: Object.freeze(['violatorName', 'violatorRosterNumber', 'violatorAssignment', 'violatorRestDays', 'subjectType', 'subjectCraftType', 'subjectSeniorityDate', 'personsInvolved', 'witnesses']),
    derived: Object.freeze([]),
  }),
  Object.freeze({
    key: 'remedy',
    title: 'What you are asking for',
    why: 'The remedy you want and the hours behind it. The platform values the claim from the agreement '
      + 'where it holds a rate, and says PLACEHOLDER where it does not - it never guesses a rate.',
    fields: Object.freeze(['remedySought', 'totalHoursClaimed', 'hourlyRateST', 'hourlyRateOT', 'totalAmountST', 'totalAmountOT']),
    derived: Object.freeze([]),
  }),
  Object.freeze({
    key: 'exhibits',
    title: 'Exhibits and evidence',
    why: 'Anything you are attaching - a bulletin, a time slip, a notice, a photograph. Attachments are '
      + 'handled on the form itself; this card is here so you can see whether you have dealt with it.',
    fields: Object.freeze([]),
    derived: Object.freeze([]),
  }),
]);

// Every section a claimant walks, identity first, in the order the claim is built.
export const ALL_SECTIONS = Object.freeze(SECTIONS.concat(CLAIM_SECTIONS));

export const SECTION_KEYS = Object.freeze(SECTIONS.map((s) => s.key));
export const ALL_SECTION_KEYS = Object.freeze(ALL_SECTIONS.map((s) => s.key));

const has = (form, k) => {
  const v = form ? form[k] : undefined;
  if (v === null || v === undefined) return false;
  return String(v).trim().length > 0;
};

export function sectionFor(key) {
  return ALL_SECTIONS.find((s) => s.key === key) || null;
}

// The state of one section. `done` means: nothing required is missing. It does NOT mean every box is
// full - a man without a job symbol can still file, and telling him the section is incomplete when the
// claim is fileable is how a form bullies someone out of filing.
export function sectionState(form, key) {
  const s = sectionFor(key);
  if (!s) return null;
  const typed = s.fields.filter((k) => s.derived.indexOf(k) === -1);
  const missingRequired = s.fields.filter((k) => REQUIRED.indexOf(k) !== -1 && !has(form, k));
  const filled = s.fields.filter((k) => has(form, k));
  const derivedKnown = s.derived.filter((k) => has(form, k));
  return {
    key: s.key,
    title: s.title,
    why: s.why,
    fields: s.fields.slice(),
    derived: s.derived.slice(),
    typedCount: typed.length,
    total: s.fields.length,
    filled: filled.length,
    missingRequired,
    done: missingRequired.length === 0,
    // A derived section is "known" when the platform has worked all three out. Shown as a statement,
    // never as a tick the member earned.
    derivedKnown: derivedKnown.length,
    derivedTotal: s.derived.length,
    // The sentence the card shows. Plain, short, and never a scold.
    summary: summaryFor(s, filled.length, missingRequired, derivedKnown.length),
  };
}

function summaryFor(s, filled, missingRequired, derivedKnown) {
  if (s.derived.length === s.fields.length) {
    if (derivedKnown === s.derived.length) return 'Worked out for you - read it over';
    if (derivedKnown === 0) return 'Not worked out yet - fill in your railroad and work location first';
    return derivedKnown + ' of ' + s.derived.length + ' worked out - the rest needs your work location';
  }
  if (missingRequired.length) {
    return 'Needs ' + missingRequired.map((k) => FIELD_LABELS[k] || k).join(' and ');
  }
  if (filled === s.fields.length) return 'Complete';
  return 'Everything required is in - ' + (s.fields.length - filled) + ' optional box'
    + (s.fields.length - filled === 1 ? '' : 'es') + ' left';
}

// The three identity sections only - what identityReady() and the old callers mean.
export function allSectionStates(form) {
  return SECTION_KEYS.map((k) => sectionState(form, k));
}

// Every section of the claim, identity and the rest.
export function allClaimSectionStates(form) {
  return ALL_SECTION_KEYS.map((k) => sectionState(form, k));
}

// Can the claim be filed as far as these three sections are concerned? The wizard's own validator still
// governs everything else; this is only about the identity, the employer and the union.
export function identityReady(form) {
  return allSectionStates(form).every((st) => st.done);
}

// What is still missing, in the words a member reads, for the one line above the three cards.
export function missingAcrossSections(form) {
  const out = [];
  for (const st of allSectionStates(form)) for (const k of st.missingRequired) out.push(FIELD_LABELS[k] || k);
  return out;
}
